import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as authApi from '../services/authApi';
import { LEGACY_WIDGET_ID_MAP } from '../config/dashboardWidgets';
import { isAdminNav, isSuperadmin } from '../utils/roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((session) => {
    if (!session?.user) {
      setUser(null);
      setRole(null);
      return;
    }
    setUser(session.user);
    setRole(session.role);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const session = await authApi.getCurrentUser();
      applySession(session);
      return session;
    } catch {
      applySession(null);
      return null;
    }
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await authApi.getCurrentUser();
        if (!cancelled) applySession(session);
      } catch {
        if (!cancelled) applySession(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const login = useCallback(async (username, password, rememberMe = false) => {
    const session = await authApi.login(username, password, rememberMe);
    applySession(session);
    return session;
  }, [applySession]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      applySession(null);
    }
  }, [applySession]);

  const roleId = role?.id || null;

  const canViewWidget = useCallback(
    (widgetId) => {
      if (!role?.widgets) return false;
      if (role.widgets.includes(widgetId)) return true;
      const legacyId = Object.entries(LEGACY_WIDGET_ID_MAP).find(
        ([, canonical]) => canonical === widgetId
      )?.[0];
      return legacyId ? role.widgets.includes(legacyId) : false;
    },
    [role]
  );

  const canAccessRoute = useCallback(
    (routeKey) => {
      if (!role?.routes) return false;
      return role.routes.includes(routeKey);
    },
    [role]
  );

  const value = useMemo(
    () => ({
      user,
      role,
      roleId,
      loading,
      isAuthenticated: !!user,
      isAdmin: isAdminNav(roleId),
      isSuperadmin: isSuperadmin(roleId),
      canManageUsers: !!role?.canManageUsers,
      canManageRoles: !!role?.canManageRoles,
      canViewWidget,
      canAccessRoute,
      login,
      logout,
      refreshSession,
    }),
    [
      user,
      role,
      roleId,
      loading,
      canViewWidget,
      canAccessRoute,
      login,
      logout,
      refreshSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

export default AuthContext;
